import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useAuth } from '@core/auth/AuthProvider';
import {
  parseAuthReturn,
  cleanAuthReturnUrl,
  saveAuthReturnFallback,
  loadAuthReturnFallback,
  clearAuthReturnFallback,
} from '@core/auth/authReturn';
import { clearFlowId, getAuthToken, setFlowId } from '@core/api';

const AUTH_RETURN_TIMEOUT_MS = 15_000;

/**
 * Lightweight auth-return handler that lives OUTSIDE TenantGuard.
 *
 * Only depends on AuthProvider (no booking, no tenant, no analytics).
 * Authenticates the user, then redirects to /auth-return-complete
 * (inside TenantGuard) which handles booking state reconstruction
 * and step navigation.
 */
export function AuthReturnGate() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { loginWithToken, authState } = useAuth();
  const processedRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const [failureReason, setFailureReason] = useState<'storage_write_failed' | 'generic' | null>(null);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const process = async () => {
      const hadAuthToken = new URLSearchParams(window.location.search).has('authToken');
      const hadLegacyToken = new URLSearchParams(window.location.search).has('token');
      const existingToken = getAuthToken();

      const fromUrl = parseAuthReturn();
      const hasUrlToken = !!fromUrl.authState?.token;
      const fromFallback = hasUrlToken ? null : loadAuthReturnFallback();
      const result = hasUrlToken ? fromUrl : (fromFallback ?? fromUrl);
      const usedFallback = !hasUrlToken && !!fromFallback;
      const flowId = result.flowId;

      if (flowId) {
        setFlowId(flowId);
      } else {
        clearFlowId();
      }

      Sentry.addBreadcrumb({
        category: 'auth-return-received',
        message: 'Token extracted',
        level: 'info',
        data: {
          flowId,
          hadAuthToken,
          hadLegacyToken,
          hadExistingTokenInStorage: !!existingToken,
          hasBookingParams: !!result.bookingParams,
          usedFallback,
        },
      });

      if (!result.authState?.token) {
        clearFlowId();
        clearAuthReturnFallback();
        cleanAuthReturnUrl();
        Sentry.addBreadcrumb({
          category: 'auth-return-received',
          message: 'No token extracted — redirecting to home',
          level: 'warning',
          data: { flowId },
        });
        navigate('/', { replace: true });
        return;
      }

      saveAuthReturnFallback(result);
      cleanAuthReturnUrl();

      await loginWithToken(result.authState.token, result.authState.email || '', flowId);

      if (!getAuthToken()) {
        Sentry.addBreadcrumb({
          category: 'auth-return-received',
          message: 'Token write readback failed — sessionStorage fallback retained for retry',
          level: 'error',
          data: { flowId },
        });
        setFailed(true);
        return;
      }

      clearAuthReturnFallback();

      const qs = new URLSearchParams();
      if (result.bookingParams) {
        const bp = result.bookingParams;
        if (bp.partySize) qs.set('ps', String(bp.partySize));
        if (bp.date) qs.set('d', bp.date);
        if (bp.serviceType) qs.set('st', bp.serviceType);
        if (bp.time) qs.set('t', bp.time);
        if (bp.flowType) qs.set('ft', bp.flowType);
        if (bp.eventSlug) qs.set('evt', bp.eventSlug);
        if (bp.isWaitingList) qs.set('wl', '1');
        if (bp.isEarlyAccess) qs.set('ea', '1');
      }

      const query = qs.toString();
      navigate(`/auth-return-complete${query ? `?${query}` : ''}`, { replace: true });
    };

    process().catch((err) => {
      clearFlowId();
      console.error('[AuthReturnGate] Error:', err);
      // Distinguish storage write failure from other errors so the user gets
      // a friendly toast on the next page load (handled by AuthErrorToast
      // mounted in providers.tsx). Other errors fall through to a silent
      // home redirect.
      const isStorageFailure =
        err instanceof Error && err.message === 'auth.token_write_readback_mismatch';
      setFailureReason(isStorageFailure ? 'storage_write_failed' : 'generic');
      setFailed(true);
    });
  }, [navigate, loginWithToken]);

  // Timeout or failure: redirect somewhere the user can recover from.
  // Storage-write failures are recoverable via the in-flow /sign-in page —
  // it's same-origin and email-only, so the WebView / storage edge cases
  // that broke the cross-subdomain flow won't break it the second time.
  // Other failures fall back to home.
  useEffect(() => {
    if (failed) {
      const target =
        failureReason === 'storage_write_failed'
          ? '/sign-in?auth_error=storage_write_failed'
          : failureReason
            ? `/?auth_error=${encodeURIComponent(failureReason)}`
            : '/';
      navigate(target, { replace: true });
      return;
    }
    const timer = setTimeout(() => {
      if (!authState.isAuthenticated) {
        navigate('/', { replace: true });
      }
    }, AUTH_RETURN_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [failed, failureReason, authState.isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <Loader2 className="w-8 h-8 animate-spin text-brand mb-4" />
      <p className="text-foreground/50 text-sm">{t('MAGIC_LINK_VERIFY_BUTTON')}</p>
    </div>
  );
}
